import { useMemo, useState } from 'react';
import {
  CaretDownIcon,
  CaretRightIcon,
  CheckCircleIcon,
  CheckIcon,
  GitDiffIcon,
  SlidersHorizontalIcon,
} from '@phosphor-icons/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { type BranchDiffFile, useGitFileDiff } from '@/api-models/sandbox-git';
import { menuItemClasses, menuLabelClasses } from '@/ui/menu-classes';
import { Spinner } from '@/ui/Spinner';
import classNames from '@/utils/classnames';

import { DiffView, type DiffViewMode } from '../changes/DiffView';
import { parseDiff } from '../changes/parseDiff';

import { DiffStat } from './DiffStat';
import { MENU_CONTENT } from './menu-classes';
import { RequestError } from './RequestError';
import type { DiffPrefs } from './review-storage';

const STATUS_STYLE: Record<string, string> = {
  Added: 'bg-diff-add-bg text-diff-add',
  Deleted: 'bg-diff-del-bg text-diff-del',
};

const VIEW_OPTIONS: readonly (readonly [DiffViewMode, string])[] = [
  ['split', 'Split view'],
  ['unified', 'Unified view'],
];

function splitPath(path: string) {
  const index = path.lastIndexOf('/');
  return { dir: index === -1 ? '' : path.slice(0, index + 1), name: index === -1 ? path : path.slice(index + 1) };
}

/** Diff display controls for the Changes tab: split/unified and hide-whitespace. */
function DiffSettingsMenu({ prefs, onChange }: { prefs: DiffPrefs; onChange: (next: DiffPrefs) => void }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          title="Diff settings"
          aria-label="Diff settings"
          className="flex h-7 items-center gap-1.5 rounded-lg bg-surface-2 px-2 text-2xs text-fg-secondary outline-none transition-colors hover:bg-surface-3 hover:text-fg focus-visible:ring-2 focus-visible:ring-accent data-[state=open]:bg-surface-3 data-[state=open]:text-fg"
        >
          <SlidersHorizontalIcon size={14} />
          <span className="font-medium text-fg">{prefs.view === 'split' ? 'Split' : 'Unified'}</span>
          {prefs.hideWhitespace && (
            <span aria-hidden title="Hide whitespace is on" className="size-1.5 rounded-full bg-accent" />
          )}
          <CaretDownIcon size={11} className="text-fg-tertiary" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} className={MENU_CONTENT}>
          <div className={menuLabelClasses}>Diff view</div>
          <DropdownMenu.RadioGroup
            value={prefs.view}
            onValueChange={(view) => onChange({ ...prefs, view: view as DiffViewMode })}
          >
            {VIEW_OPTIONS.map(([value, label]) => (
              <DropdownMenu.RadioItem
                key={value}
                value={value}
                className={classNames(menuItemClasses, 'data-[state=checked]:font-medium data-[state=checked]:text-fg')}
              >
                <span className="flex-1">{label}</span>
                <DropdownMenu.ItemIndicator>
                  <CheckIcon size={15} weight="bold" className="text-accent" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
          <div className="my-1 h-px bg-line-subtle" />
          <DropdownMenu.CheckboxItem
            checked={prefs.hideWhitespace}
            onCheckedChange={(checked) => onChange({ ...prefs, hideWhitespace: checked === true })}
            onSelect={(e) => e.preventDefault()}
            className={menuItemClasses}
          >
            <span className="flex-1">Hide whitespace</span>
            {prefs.hideWhitespace && <CheckIcon size={15} weight="bold" className="text-accent" />}
          </DropdownMenu.CheckboxItem>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

interface FileCardProps {
  base: string;
  path: string;
  status: string;
  viewed: boolean;
  view: DiffViewMode;
  hideWhitespace: boolean;
  onToggleViewed: () => void;
}

function FileCard({ base, path, status, viewed, view, hideWhitespace, onToggleViewed }: FileCardProps) {
  const [expanded, setExpanded] = useState(false);
  const { diff, isLoading } = useGitFileDiff(base, expanded ? path : null);
  const parsed = useMemo(() => (diff ? parseDiff(diff) : null), [diff]);
  const { dir, name } = splitPath(path);

  return (
    <div className="overflow-hidden rounded-lg border border-line-subtle bg-surface-2">
      <div className={classNames('flex items-center gap-2 px-3 py-2', viewed && 'opacity-55')}>
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
          aria-expanded={expanded}
        >
          {expanded ? <CaretDownIcon size={12} /> : <CaretRightIcon size={12} />}
          <span className="truncate text-xs text-fg">{name}</span>
          {dir && <span className="truncate text-2xs text-fg-tertiary">{dir}</span>}
        </button>
        <span
          className={classNames(
            'shrink-0 rounded px-1.5 py-0.5 text-2xs',
            STATUS_STYLE[status] ?? 'bg-surface-3 text-fg-secondary',
          )}
        >
          {status}
        </span>
        {parsed && (
          <DiffStat
            additions={parsed.additions}
            deletions={parsed.deletions}
            className="shrink-0 text-2xs tabular-nums"
          />
        )}
        <button
          type="button"
          onClick={onToggleViewed}
          title={viewed ? 'Viewed' : 'Mark as viewed'}
          className={classNames(
            'flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-2xs outline-none focus-visible:ring-2 focus-visible:ring-accent',
            viewed ? 'text-diff-add' : 'text-fg-tertiary hover:text-fg-secondary',
          )}
        >
          {viewed ? <CheckCircleIcon size={14} weight="fill" /> : <CheckIcon size={14} />}
          {viewed ? 'Viewed' : 'Mark as viewed'}
        </button>
      </div>
      {expanded &&
        (isLoading || !parsed ? (
          <div className="flex items-center justify-center border-t border-line-subtle py-4">
            <Spinner size={5} />
          </div>
        ) : (
          <div className="border-t border-line-subtle">
            <DiffView path={path} hunks={parsed.hunks} view={view} hideWhitespace={hideWhitespace} />
          </div>
        ))}
    </div>
  );
}

interface ChangesPanelProps {
  base: string;
  files: BranchDiffFile[];
  isLoading: boolean;
  error: unknown;
  /** Paths the reviewer has checked off. */
  viewed: string[];
  onViewedChange: (next: string[]) => void;
  diffPrefs: DiffPrefs;
  onDiffPrefsChange: (next: DiffPrefs) => void;
}

/** The Changes sub-tab: every file the branch touches, unviewed first, each diff expandable. */
export function ChangesPanel({
  base,
  files,
  isLoading,
  error,
  viewed,
  onViewedChange,
  diffPrefs,
  onDiffPrefsChange,
}: ChangesPanelProps) {
  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size={5} />
      </div>
    );
  }

  if (error) return <RequestError error={error} what="changed files" />;

  if (files.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center text-sm text-fg-tertiary">
        <GitDiffIcon size={24} className="text-fg-tertiary" />
        No changes on this branch yet.
      </div>
    );
  }

  const viewedPaths = new Set(viewed);
  const toggle = (path: string) =>
    onViewedChange(viewedPaths.has(path) ? viewed.filter((p) => p !== path) : [...viewed, path]);

  const unviewedFiles = files.filter((f) => !viewedPaths.has(f.path));
  const viewedFiles = files.filter((f) => viewedPaths.has(f.path));

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="flex items-center gap-2 px-3 pt-3 text-2xs text-fg-tertiary">
        <span>
          {viewedFiles.length} of {files.length} files viewed
        </span>
        {viewedFiles.length > 0 && (
          <button
            type="button"
            onClick={() => onViewedChange([])}
            className="rounded outline-none hover:text-fg-secondary focus-visible:ring-2 focus-visible:ring-accent"
          >
            Reset viewed
          </button>
        )}
        <div className="ml-auto">
          <DiffSettingsMenu prefs={diffPrefs} onChange={onDiffPrefsChange} />
        </div>
      </div>
      <div className="flex flex-col gap-2 p-3">
        {unviewedFiles.map((file) => (
          <FileCard
            key={file.path}
            base={base}
            path={file.path}
            status={file.status}
            viewed={false}
            view={diffPrefs.view}
            hideWhitespace={diffPrefs.hideWhitespace}
            onToggleViewed={() => toggle(file.path)}
          />
        ))}

        {viewedFiles.length > 0 && (
          <>
            <div className="px-1 pt-2 text-2xs font-semibold tracking-wide text-fg-tertiary">
              VIEWED ({viewedFiles.length})
            </div>
            {viewedFiles.map((file) => (
              <FileCard
                key={file.path}
                base={base}
                path={file.path}
                status={file.status}
                viewed
                view={diffPrefs.view}
                hideWhitespace={diffPrefs.hideWhitespace}
                onToggleViewed={() => toggle(file.path)}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
